import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  AideEnBulle,
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { ChoixPersonnel } from '../components/ChoixPersonnel';
import { SalairesContrat } from '../components/SalairesContrat';
import { TransportsContrat } from '../components/TransportsContrat';
import { IconeMutuelle, IconeTransport } from '../components/IconesContrat';
import { formaterDate } from '../lib/format';
import {
  BASE_TEMPS_PLEIN,
  MOTIFS_FIN_CONTRAT,
  STATUTS_CONTRAT,
  TEMPS_TRAVAIL,
  libelleMotifFin,
  libelleStatutContrat,
  libelleTempsTravail,
  natureRemuneration,
} from '../lib/contrat';
import type { Contrat, Personnel, StatutContrat, TempsTravail } from '../types';

/**
 * Contrats de travail de l'entité active (menu Équipe → Contrats).
 *
 * Données de PAIE : l'accès est réservé à `contrat.lire` (Direction), et
 * chaque consultation est journalisée côté serveur, même en lecture (CDC
 * Paie §4). La table est cloisonnée par RLS — pour voir les contrats de
 * l'autre école, on bascule d'entité.
 *
 * L'ancienneté, les points et l'ETP arrivent CALCULÉS, aux valeurs
 * conventionnelles en vigueur (D6) : les saisir les figerait.
 */
export function ContratsPage() {
  const { peut, entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';
  const [parametres, setParametres] = useSearchParams();
  // `?personnel=<id>` filtre la liste : c'est le lien qu'offre l'écran
  // Personnel, et il rend la page partageable.
  const personnelFiltre = parametres.get('personnel') ?? '';

  const [edition, setEdition] = useState<Contrat | null>(null);
  const [creation, setCreation] = useState(false);
  // Les contrats ACTIFS seulement, par defaut (2026-10-02) : c'est
  // l'effectif du moment qu'on vient regarder, les contrats clos restent
  // a une case a cocher.
  const [actifsSeulement, setActifsSeulement] = useState(true);

  const contrats = useRessource(() => api.get<Contrat[]>('/api/contrats'), [cle]);
  const personnel = useRessource(() => api.get<Personnel[]>('/api/personnel'), [cle]);

  if (contrats.chargement) return <Chargement />;

  const liste = (contrats.donnees ?? []).filter(
    (c) => !personnelFiltre || c.personnel_id === personnelFiltre,
  );
  const enCours = liste.filter((c) => c.en_cours);
  const affiches = actifsSeulement ? enCours : liste;
  const filtreNom = personnel.donnees?.find((s) => s.id === personnelFiltre);

  // Un contrat CLOS reste lisible mais s'efface : le gris est porte par
  // chaque cellule, le composant Tableau ne teintant pas ses lignes.
  const ton = (contrat: Contrat) => (contrat.en_cours ? '' : 'text-encre-400');

  // PAS DE COLONNE D'ACTIONS (2026-10-02). La ligne entiere ouvre la fiche
  // du contrat, et il n'y a plus de lien « Clore » : on clot un contrat en
  // renseignant sa fin effective et son motif, dans le bloc « Fin de
  // contrat » de la fiche — un seul endroit pour dire qu'un contrat finit,
  // et pourquoi.
  const colonnes: Colonne<Contrat>[] = [
    {
      cle: 'salarie',
      entete: 'Salarié',
      rendu: (contrat) => (
        <span className={`flex flex-wrap items-center gap-1.5 ${ton(contrat)}`}>
          <span>
            <span className="font-medium">{contrat.personnel_prenom_usuel}</span>{' '}
            <span className="uppercase">{contrat.personnel_nom ?? ''}</span>
          </span>
          {/* Mutuelle et transport en icones, le detail en infobulle. */}
          {contrat.mutuelle && <IconeMutuelle depuis={contrat.date_souscription_mutuelle} />}
          {(contrat.titre_transport ?? 0) > 0 && (
            <IconeTransport montant={contrat.titre_transport!} type={contrat.titre_transport_type} />
          )}
        </span>
      ),
    },
    {
      cle: 'contrat',
      entete: 'Contrat',
      rendu: (contrat) => (
        <span className={`flex flex-wrap items-center gap-1.5 ${ton(contrat)}`}>
          {/* Le CDI garde sa pastille pleine ; le CDD se lit en creux —
              fond blanc, lisere et texte verts, meme taille — pour que les
              deux types se distinguent d'un coup d'oeil. Termine, l'un
              comme l'autre passe sur fond gris. */}
          {/* Largeur FIXE : « CDD » est plus large que « CDI » d'un glyphe,
              et deux pastilles de largeur inegale cassent l'alignement de
              la colonne. */}
          <Badge
            ton={!contrat.en_cours ? 'neutre' : contrat.type === 'cdd' ? 'succesContour' : 'succes'}
            classe="w-11 justify-center"
          >
            {contrat.type.toUpperCase()}
          </Badge>
          {libelleStatutContrat(contrat.statut)}
          {/* Le temps de travail ne se dit que s'il fait un salaire fixe :
              le reel est le cas courant. */}
          {natureRemuneration(contrat.statut, contrat.temps_travail) !== 'reel' && (
            <span className="text-xs text-encre-500">
              · {libelleTempsTravail(contrat.temps_travail).toLowerCase()}
            </span>
          )}
        </span>
      ),
    },
    {
      cle: 'periode',
      entete: 'Période',
      classe: 'tabulaire whitespace-nowrap text-xs',
      // TOUT SUR UNE LIGNE : le terme ou le motif suivent la periode apres
      // un point median, pour que chaque ligne du tableau ait la meme
      // hauteur — une seconde ligne sur les seuls contrats a fin les
      // faisait ressortir.
      rendu: (contrat) => (
        <span className={ton(contrat)}>
          {formaterDate(contrat.date_debut)}
          {/* UN CDD EN COURS a deja sa date de fin — c'est son terme, pas
              une sortie. La periode dit donc « en cours », et le terme
              suit. Clos, il s'ecrit comme les autres. */}
          {contrat.type === 'cdd' && contrat.en_cours && contrat.date_fin ? (
            <>
              {' → en cours'}
              <span className="text-encre-500"> · Fin de CDD le {formaterDate(contrat.date_fin)}</span>
            </>
          ) : (
            <>
              {contrat.date_fin ? ` → ${formaterDate(contrat.date_fin)}` : ' → en cours'}
              {contrat.motif_fin && (
                <span className="text-encre-500"> · {libelleMotifFin(contrat.motif_fin)}</span>
              )}
            </>
          )}
        </span>
      ),
    },
    {
      cle: 'anciennete',
      entete: 'Ancienneté',
      classe: 'tabulaire text-right',
      rendu: (contrat) => <span className={ton(contrat)}>{contrat.anciennete_mois} mois</span>,
    },
    {
      cle: 'points',
      entete: 'Points',
      classe: 'tabulaire text-right',
      rendu: (contrat) => <span className={ton(contrat)}>{contrat.points_anciennete}</span>,
    },
  ];

  return (
    <>
      <EnTetePage
        titre={
          <>
            Contrats
            <AideEnBulle texte="Un salarié a un contrat par entité, avec sa propre ancienneté." />
          </>
        }
        actions={
          peut('contrat.gerer') ? (
            <button type="button" className="bouton-principal" onClick={() => setCreation(true)}>
              Nouveau contrat
            </button>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-encre-500">
        <span>
          <span className="tabulaire font-medium text-encre-900">{enCours.length}</span> en cours
          {liste.length !== enCours.length && ` · ${liste.length - enCours.length} clos`}
        </span>
        <label className="flex items-center gap-2 text-encre-600">
          <input
            type="checkbox"
            checked={actifsSeulement}
            onChange={(evenement) => setActifsSeulement(evenement.target.checked)}
          />
          Contrats actifs seulement
        </label>
        {filtreNom && (
          <Infobulle texte="Retirer le filtre" className="inline-flex">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full bg-encre-100 px-2 py-0.5 text-xs text-encre-700 hover:bg-encre-200"
              onClick={() => setParametres({})}
            >
              {filtreNom.prenom_usuel} {filtreNom.nom ?? ''} <span aria-hidden>×</span>
            </button>
          </Infobulle>
        )}
        <Link to="/personnel" className="ml-auto text-xs underline">
          Voir le personnel
        </Link>
      </div>

      {contrats.erreur && <Erreur message={contrats.erreur} onReessayer={contrats.recharger} />}

      {affiches.length === 0 ? (
        <p className="rounded-lg border border-dashed border-encre-200 px-4 py-3 text-sm text-encre-500">
          Aucun contrat{actifsSeulement ? ' actif' : ''}
          {filtreNom ? ' pour ce salarié' : ''} dans cette entité.
          {actifsSeulement && liste.length > 0 && ' Décochez « Contrats actifs seulement » pour voir les contrats clos.'}
        </p>
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={affiches}
            cleDe={(c) => c.id}
            onLigneCliquee={peut('contrat.gerer') ? (c) => setEdition(c) : undefined}
          />
        </div>
      )}

      <p className="mt-4 text-xs text-encre-400">
        Ancienneté et points sont ceux du mois en cours, depuis le premier contrat dans l’école :
        2 points par année complète dès 24 mois, acquis au mois anniversaire de l’entrée (point à
        7,15 €). Un contrat au cachet est payé hors plateforme : aucune ligne de paie n’en
        découlera.
      </p>

      {(creation || edition) && (
        <FormulaireContrat
          personnel={personnel.donnees ?? []}
          contrat={edition}
          onFermer={() => {
            setCreation(false);
            setEdition(null);
          }}
          onEnregistre={() => {
            setCreation(false);
            setEdition(null);
            contrats.recharger();
          }}
        />
      )}
    </>
  );
}

/** Un bloc du formulaire : un cadre, un titre court. */
function Bloc({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3 rounded-md border border-encre-200 p-3">
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">{titre}</legend>
      {children}
    </fieldset>
  );
}

/**
 * Création et correction d'un contrat, en CINQ BLOCS (2026-10-02) : type
 * et date d'entrée, mutuelle et transport, statut et rémunération, fin de
 * contrat, commentaire.
 *
 * À la création, le salarié se choisit par le champ saisissable — le même
 * que pour les créneaux ; en correction il est dans le titre, pas
 * modifiable : changer le titulaire d'un contrat, c'est un autre contrat.
 *
 * Le bloc de rémunération suit le STATUT et le TEMPS DE TRAVAIL : un
 * salaire mensuel par période pour un administratif — avec ses heures par
 * semaine s'il est à temps partiel — et pour un professeur ou un animateur
 * à temps plein ; une assiette pour ces derniers à temps partiel, payés au
 * réel sur leurs cours. Le tableau des salaires écrit immédiatement, selon
 * le contrat ENREGISTRÉ : il n'apparaît donc qu'une fois le statut et le
 * temps de travail enregistrés.
 *
 * Un CDD a un terme : sa fin effective est obligatoire et son motif vaut
 * « Fin de CDD ». Le serveur et la base tiennent la même règle.
 */
export function FormulaireContrat({
  personnel,
  contrat,
  personnelImpose = null,
  onFermer,
  onEnregistre,
}: {
  personnel: Personnel[];
  contrat: Contrat | null;
  /** Depuis la fiche d'un salarie (2026-10-03) : le titulaire est connu, le champ ne se montre pas. */
  personnelImpose?: Personnel | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const { peut } = useAuth();
  const [personnelId, setPersonnelId] = useState('');
  const [valeurs, setValeurs] = useState({
    type: 'cdi',
    statut: 'animateur' as StatutContrat,
    tempsTravail: 'partiel' as TempsTravail,
    baseForfaitaire: true,
    dateDebut: '',
    dateFin: '',
    motifFin: '',
    assiette: '12',
    mutuelle: false,
    dateSouscription: '',
    commentaire: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = contrat?.id ?? 'nouveau';
  if (cle !== initialisePour) {
    setInitialisePour(cle);
    setPersonnelId(contrat?.personnel_id ?? personnelImpose?.id ?? '');
    setValeurs({
      type: contrat?.type ?? 'cdi',
      statut: contrat?.statut ?? 'animateur',
      tempsTravail: contrat?.temps_travail ?? 'partiel',
      baseForfaitaire: contrat?.base_forfaitaire ?? true,
      dateDebut: contrat?.date_debut ?? '',
      dateFin: contrat?.date_fin ?? '',
      motifFin: contrat?.motif_fin ?? '',
      assiette: String(contrat?.assiette_mois ?? 12),
      mutuelle: contrat?.mutuelle ?? false,
      dateSouscription: contrat?.date_souscription_mutuelle ?? '',
      commentaire: contrat?.commentaire ?? '',
    });
    setErreur(null);
  }

  const estCdd = valeurs.type === 'cdd';
  const estAdministratif = valeurs.statut === 'administratif';
  const nature = natureRemuneration(valeurs.statut, valeurs.tempsTravail);
  // Le tableau des salaires ecrit sur le contrat tel qu'il est EN BASE :
  // tant que le statut ou le temps de travail affiches different de ceux
  // enregistres, le serveur jugerait la saisie avec les anciens.
  const remunerationEnregistree =
    contrat !== null &&
    contrat.statut === valeurs.statut &&
    contrat.temps_travail === valeurs.tempsTravail;

  // Changer de FAMILLE de statut repose le temps de travail sur son
  // defaut : plein pour un administratif, partiel (au reel) sinon — et
  // la base forfaitaire sur le sien : eligible pour un professeur ou un
  // animateur, jamais pour un administratif.
  const changerStatut = (statut: StatutContrat) => {
    const changeDeFamille = (statut === 'administratif') !== estAdministratif;
    setValeurs({
      ...valeurs,
      statut,
      tempsTravail: changeDeFamille
        ? statut === 'administratif'
          ? 'plein'
          : 'partiel'
        : valeurs.tempsTravail,
      baseForfaitaire: changeDeFamille ? statut !== 'administratif' : valeurs.baseForfaitaire,
    });
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!contrat && !personnelId) {
      setErreur('Choisissez le salarié.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        type: valeurs.type,
        statut: valeurs.statut,
        tempsTravail: valeurs.tempsTravail,
        baseForfaitaire: estAdministratif ? false : valeurs.baseForfaitaire,
        dateDebut: valeurs.dateDebut,
        dateFin: valeurs.dateFin || null,
        // Un CDD se termine par son terme : le motif est impose.
        motifFin: estCdd ? 'fin_cdd' : valeurs.motifFin || null,
        assietteMois: Number(valeurs.assiette) || 12,
        mutuelle: valeurs.mutuelle,
        dateSouscriptionMutuelle: valeurs.mutuelle ? valeurs.dateSouscription || null : null,
        commentaire: valeurs.commentaire || null,
      };
      if (contrat) {
        await api.patch(`/api/contrats/${contrat.id}`, corps);
      } else {
        await api.post('/api/contrats', { personnelId, ...corps });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={
        contrat ? (
          <>
            Contrat de {contrat.personnel_prenom_usuel}{' '}
            <span className="uppercase">{contrat.personnel_nom ?? ''}</span>{' '}
            <Badge ton={contrat.en_cours ? 'succes' : 'neutre'}>
              {contrat.en_cours ? 'en cours' : 'terminé'}
            </Badge>
          </>
        ) : (
          'Nouveau contrat'
        )
      }
      titreAccessible={
        contrat
          ? `Contrat de ${contrat.personnel_prenom_usuel} ${contrat.personnel_nom ?? ''}`
          : personnelImpose
            ? `Nouveau contrat pour ${personnelImpose.prenom_usuel} ${personnelImpose.nom ?? ''}`
            : 'Nouveau contrat'
      }
      ouverte
      onFermer={onFermer}
      large
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Bloc titre="Type et date d’entrée">
          {!contrat && !personnelImpose && (
            <Champ libelle="Salarié">
              <ChoixPersonnel
                personnel={personnel}
                valeur={personnelId}
                placeholder="Taper un nom…"
                onChanger={setPersonnelId}
              />
            </Champ>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <Champ libelle="Type de contrat">
              <select
                className="champ"
                value={valeurs.type}
                onChange={(e) => setValeurs({ ...valeurs, type: e.target.value })}
              >
                <option value="cdi">CDI</option>
                <option value="cdd">CDD</option>
              </select>
            </Champ>
            <Champ libelle="Date d’entrée" aide="C’est d’elle que se calcule l’ancienneté.">
              <input
                className="champ"
                type="date"
                value={valeurs.dateDebut}
                onChange={(e) => setValeurs({ ...valeurs, dateDebut: e.target.value })}
                required
              />
            </Champ>
            {contrat && (
              <div>
                <span className="etiquette">Ancienneté</span>
                <p className="tabulaire py-2 text-sm">
                  {contrat.anciennete_mois} mois · {contrat.points_anciennete}{' '}
                  {contrat.points_anciennete === 1 ? 'point' : 'points'}
                </p>
              </div>
            )}
          </div>
        </Bloc>

        <Bloc titre="Mutuelle et transport">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <span className="etiquette">Mutuelle</span>
              <label className="flex items-center gap-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={valeurs.mutuelle}
                  onChange={(e) => setValeurs({ ...valeurs, mutuelle: e.target.checked })}
                />
                Souscrite
              </label>
            </div>
            {valeurs.mutuelle ? (
              <Champ libelle="Souscrite depuis le" aide="La cotisation court de cette date (RS-26).">
                <input
                  className="champ"
                  type="date"
                  value={valeurs.dateSouscription}
                  onChange={(e) => setValeurs({ ...valeurs, dateSouscription: e.target.value })}
                  required
                />
              </Champ>
            ) : (
              <span />
            )}
          </div>
          {/* LES TITRES DE TRANSPORT PAR PERIODE (2026-11-03) : le champ
              unique a disparu, le bloc ecrit tout seul et n'existe
              qu'une fois le contrat enregistre, comme le salaire. */}
          <div className="mt-4 border-t border-encre-100 pt-4">
            {contrat ? (
              <TransportsContrat contratId={contrat.id} peutGerer={peut('contrat.gerer')} />
            ) : (
              <p className="text-xs text-encre-500">
                Les titres de transport se saisissent une fois le contrat enregistré : enregistrez-le, puis
                rouvrez-le.
              </p>
            )}
          </div>
        </Bloc>

        <Bloc titre="Statut et rémunération">
          <div className="grid gap-3 sm:grid-cols-3">
            <Champ libelle="Statut">
              <select
                className="champ"
                value={valeurs.statut}
                onChange={(e) => changerStatut(e.target.value as StatutContrat)}
              >
                {STATUTS_CONTRAT.map((s) => (
                  <option key={s.valeur} value={s.valeur}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Temps de travail">
              <select
                className="champ"
                value={valeurs.tempsTravail}
                onChange={(e) => setValeurs({ ...valeurs, tempsTravail: e.target.value as TempsTravail })}
              >
                {TEMPS_TRAVAIL.map((t) => (
                  <option key={t.valeur} value={t.valeur}>
                    {t.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            {nature === 'reel' ? (
              <Champ libelle="Assiette (mois)" aide="Réévaluée chaque année par le calcul.">
                <input
                  className="champ tabulaire"
                  type="number"
                  min={1}
                  max={12}
                  value={valeurs.assiette}
                  onChange={(e) => setValeurs({ ...valeurs, assiette: e.target.value })}
                />
              </Champ>
            ) : (
              <div>
                <span className="etiquette">Base temps plein</span>
                <p className="tabulaire py-2 text-sm">{BASE_TEMPS_PLEIN[valeurs.statut]} h / semaine</p>
              </div>
            )}
            {/* Professeur ou animateur seulement : un administratif n'y est
                jamais eligible, le champ ne se montre pas (2026-11-02). */}
            {!estAdministratif && (
              <div>
                <span className="etiquette">Base forfaitaire</span>
                <label className="flex items-center gap-2 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={valeurs.baseForfaitaire}
                    onChange={(e) => setValeurs({ ...valeurs, baseForfaitaire: e.target.checked })}
                  />
                  Éligible à la base forfaitaire
                </label>
              </div>
            )}
          </div>
          {nature === 'reel' ? (
            <p className="text-xs text-encre-500">
              Paie au réel : elle se calcule depuis les créneaux, dans l’écran Paie. Base temps plein de
              référence : {BASE_TEMPS_PLEIN[valeurs.statut]} h / semaine.
            </p>
          ) : (
            <>
              {nature === 'fixe_cours' && (
                <p className="text-xs text-encre-500">
                  Salaire fixe : ses créneaux ne produisent plus d’éléments de paie. La prime d’ancienneté
                  s’ajoute dans le détail de paie.
                </p>
              )}
              {contrat && remunerationEnregistree ? (
                <SalairesContrat
                  contratId={contrat.id}
                  peutGerer={peut('contrat.gerer')}
                  avecHeures={estAdministratif && valeurs.tempsTravail === 'partiel'}
                  plafondHeures={BASE_TEMPS_PLEIN.administratif}
                />
              ) : (
                <p className="text-xs text-encre-500">
                  Le salaire mensuel se saisit une fois le contrat enregistré : enregistrez-le, puis
                  rouvrez-le.
                </p>
              )}
            </>
          )}
        </Bloc>

        <Bloc titre="Fin de contrat">
          <div className="grid gap-3 sm:grid-cols-3">
            <Champ
              libelle="Fin effective"
              aide={
                estCdd
                  ? 'Obligatoire pour un CDD. Elle arrête la paie.'
                  : 'Elle arrête la paie. Vide : contrat en cours.'
              }
            >
              <input
                className="champ"
                type="date"
                value={valeurs.dateFin}
                min={valeurs.dateDebut || undefined}
                required={estCdd}
                onChange={(e) => setValeurs({ ...valeurs, dateFin: e.target.value })}
              />
            </Champ>
            <Champ libelle="Motif de fin">
              <select
                className="champ disabled:bg-encre-100 disabled:text-encre-500"
                value={estCdd ? 'fin_cdd' : valeurs.motifFin}
                disabled={estCdd}
                onChange={(e) => setValeurs({ ...valeurs, motifFin: e.target.value })}
              >
                <option value="">— Non renseigné —</option>
                {MOTIFS_FIN_CONTRAT.filter((m) => estCdd || m.valeur !== 'fin_cdd').map((m) => (
                  <option key={m.valeur} value={m.valeur}>
                    {m.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          </div>
        </Bloc>

        <Bloc titre="Commentaire">
          <input
            className="champ"
            aria-label="Commentaire"
            placeholder="Note libre"
            value={valeurs.commentaire}
            onChange={(e) => setValeurs({ ...valeurs, commentaire: e.target.value })}
          />
        </Bloc>

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          libelle={contrat ? 'Enregistrer' : 'Créer le contrat'}
        />
      </form>
    </Modale>
  );
}
